/* Permisos dados que no abren nada porque les falta otro.
 *
 * ── EL CASO QUE LO TRAJO ────────────────────────────────────────────────────
 *
 * Se le dio a alguien `ver.ticketera.furgones` para que viera sólo el taller de
 * furgones, y no pasó nada. El permiso estaba guardado —se veía en la base— y
 * la pantalla decía «1 cambio aplicado», que era cierto. Pero las dos llaves de
 * alcance (`.furgones` / `.tractores`) ACOTAN la ticketera, y la que la ABRE es
 * `ver.ticketera`. Sin la puerta, el alcance no tiene sobre qué aplicarse.
 *
 * Lo que hace que esto valga un módulo no es el caso: es el MODO DE FALLA. No
 * falla en ningún lado. El guardado anda, el mensaje es verdadero, y la persona
 * sigue sin ver nada — así que el síntoma («no se aplica») manda a buscar el
 * problema al guardado, que es lo único que estaba bien.
 *
 * Misma familia que los permisos muertos que ya se limpiaron de la base: **una
 * llave que no abre nada es peor que una llave que falta**, porque la que falta
 * se pide y la que no abre se da por hecha. La diferencia es que aquéllas no
 * abrían nada nunca y éstas no abren nada SOLAS.
 *
 * ── LA REGLA NO VIVE ACÁ ────────────────────────────────────────────────────
 *
 * `depende_de` es una columna de `permisos`, así que la dependencia es un DATO
 * y no una lista escrita en el JavaScript del hub. Tiene que ser así porque la
 * ven los dos lados: el hub dibuja el menú y FlotaBot hace valer
 * `ver.ticketera` en `/taller`. Escrita acá, el día que se agregue otro par así
 * FlotaBot no se enteraría.
 *
 * Este módulo sólo CRUZA lo que la base ya dice con lo que cada persona tiene.
 *
 * ── SE AVISA, NO SE ARREGLA SOLO ────────────────────────────────────────────
 *
 * Dar el permiso que falta sin preguntar sería abrirle a alguien una pantalla
 * entera que nadie decidió darle: `ver.ticketera` abre los DOS talleres, y el
 * que estaba dando el de furgones justamente no quería eso hasta acotarlo. Se
 * muestra qué falta y con un click se completa. Misma regla que `duplicados`,
 * el cotejo y `mecanicos.parecidos()`.
 */

/* Las llaves muertas de UNA persona.
 *
 * `permisos`  las filas de la tabla `permisos` (con `clave` y `depende_de`)
 * `tiene`     las claves que esa persona tiene
 *
 * Devuelve `[{ clave, etiqueta, falta, faltaEtiqueta }]`, vacío si está todo
 * bien. Sólo mira lo que la persona TIENE: un permiso que no se le dio no es
 * una llave muerta, es un permiso que no se le dio.
 */
export function muertas(permisos, tiene) {
  const dadas = new Set(tiene || []);
  const porClave = new Map((permisos || []).map((p) => [p.clave, p]));

  return (permisos || [])
    .filter((p) => p.depende_de && dadas.has(p.clave) && !dadas.has(p.depende_de))
    .map((p) => ({
      clave: p.clave,
      etiqueta: p.etiqueta || p.clave,
      falta: p.depende_de,
      faltaEtiqueta: porClave.get(p.depende_de)?.etiqueta || p.depende_de,
    }));
}

/* Lo mismo para varias personas, agrupado por lo que hay que dar.
 *
 * Con cuatro personas seleccionadas y la misma llave muerta en las cuatro, un
 * aviso por persona son cuatro carteles que dicen lo mismo y se leen como
 * cuatro problemas distintos. Se agrupa por el permiso que FALTA, que es la
 * unidad con la que se arregla: un click lo resuelve para todas.
 */
export function muertasDeVarias(permisos, personas) {
  const porFalta = new Map();

  for (const persona of personas || []) {
    for (const m of muertas(permisos, persona.tiene)) {
      const item = porFalta.get(m.falta) || {
        falta: m.falta,
        faltaEtiqueta: m.faltaEtiqueta,
        inutiles: new Set(),
        personas: [],
      };
      item.inutiles.add(m.etiqueta);
      // El nombre importa: con varias seleccionadas hay que saber a cuál le
      // pasa, o el aviso obliga a revisarlas una por una.
      const quien = persona.nombre || persona.mail;
      if (quien && !item.personas.includes(quien)) item.personas.push(quien);
      porFalta.set(m.falta, item);
    }
  }

  return [...porFalta.values()].map((i) => ({
    ...i,
    inutiles: [...i.inutiles].sort(),
  }));
}
